import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Calculator } from "lucide-react";

/** In-article interactive DSCR check. Illustrative math only — not a quote. */
export function QuickDscrCheck() {
  const [rent, setRent] = useState(3200);
  const [pitia, setPitia] = useState(2500);
  const dscr = pitia > 0 ? rent / pitia : 0;
  const tone =
    dscr >= 1.25 ? "Strong cash-flow coverage" : dscr >= 1 ? "Rent covers the payment" : "Rent falls short of the payment";

  const field = (label: string, value: number, set: (n: number) => void, max: number) => (
    <label className="block text-sm">
      <span className="flex justify-between font-medium text-foreground">
        {label} <span>${value.toLocaleString()}</span>
      </span>
      <input
        type="range"
        min={500}
        max={max}
        step={50}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="mt-2 w-full accent-primary"
        aria-label={label}
      />
    </label>
  );

  return (
    <aside className="not-prose my-10 rounded-xl border border-border bg-card p-6 shadow-sm">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
        <Calculator className="h-4 w-4" aria-hidden /> Quick DSCR check
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Slide the numbers to see how a lender would read this rental's cash flow.
      </p>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {field("Monthly rent", rent, setRent, 15000)}
        {field("Monthly payment (PITIA)", pitia, setPitia, 15000)}
      </div>
      <div className="mt-6 flex flex-col gap-3 rounded-lg bg-muted p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-3xl font-black text-foreground">{dscr.toFixed(2)}x</p>
          <p className="text-sm text-muted-foreground">{tone}</p>
        </div>
        <Link to="/dscr-calculator" className="text-sm font-semibold text-primary hover:underline">
          Run the full DSCR calculator →
        </Link>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Illustrative only: DSCR = rent ÷ PITIA. Not an offer of credit; qualification depends on lender guidelines,
        credit, appraisal, and full underwriting. Business-purpose investment property only.
      </p>
    </aside>
  );
}
